import {
  alignRange,
  defaultHeatmapRange,
  granularitySchema,
  isoDateSchema,
  queryList,
  type HeatmapRange,
} from '@pm-tool/shared';
import { z } from 'zod';
import type { Granularity } from './heatmap-format';

/**
 * URL state of the heatmaps (decision 7c: shareable, survives reload). Invalid values are
 * dropped one by one, so an old or edited link still opens the view.
 */
export type HeatmapSearch = {
  g?: Granularity;
  from?: string;
  to?: string;
  unit?: 'hours' | 'percent';
  /**
   * People view: group by primary job role (default) or its area (RES-15); role view: by area
   * (default while areas are on) or not at all.
   */
  group?: HeatmapGroup;
  role?: string;
  /** People (or job roles) of this area (RES-15). */
  area?: string;
  /** People having all of these skills (RES-16). */
  skills?: string[];
  project?: string;
};

export type HeatmapGroup = 'role' | 'area' | 'none';

const fieldSchemas = {
  g: granularitySchema,
  from: isoDateSchema,
  to: isoDateSchema,
  unit: z.enum(['hours', 'percent']),
  group: z.enum(['role', 'area', 'none']),
  role: z.uuid(),
  area: z.uuid(),
  skills: queryList(z.uuid()).refine((ids) => ids.length > 0),
  project: z.uuid(),
};

export function parseHeatmapSearch(raw: Record<string, unknown>): HeatmapSearch {
  const result: Record<string, unknown> = {};
  for (const [key, schema] of Object.entries(fieldSchemas)) {
    const value = raw[key];
    if (value === undefined || value === '') continue;
    const parsed = (schema as z.ZodType).safeParse(value); // each entry is a Zod schema
    if (parsed.success) result[key] = parsed.data;
  }
  return result;
}

export type HeatmapView = {
  granularity: Granularity;
  range: HeatmapRange;
  unit: 'hours' | 'percent';
  group: HeatmapGroup;
};

/** Defaults: weeks, the current week and the 12 following, hours, grouped by role. */
export function resolveHeatmapSearch(search: HeatmapSearch, today: string): HeatmapView {
  const granularity = search.g ?? 'week';
  const fallback = defaultHeatmapRange(today);
  const range = alignRange(
    { from: search.from ?? fallback.from, to: search.to ?? search.from ?? fallback.to },
    granularity,
  );
  return {
    granularity,
    range,
    unit: search.unit ?? 'hours',
    group: search.group ?? 'role',
  };
}
